import { useEffect, useMemo, useRef, useCallback } from "react";
import { isProtectionEnabled, getAuditApiUrl } from "./protectionConfig";
import { resolveRShieldConfig } from "./rShieldConfig";
import { useFocusLossLimit } from "./useFocusLossLimit";
import { createAuditLogger, AuditEvents } from "./auditLogger";
import { createRShieldFocusReporter } from "./rShieldFocusReporter";
import { useFullscreen } from "./useFullscreen";
import { useWindowFocus } from "./useWindowFocus";
import { usePageVisibility } from "./usePageVisibility";
import { useContextMenuGuard } from "./useContextMenuGuard";
import { useTextSelectionGuard } from "./useTextSelectionGuard";
import { useDragDropGuard } from "./useDragDropGuard";
import { useKeyboardGuard } from "./useKeyboardGuard";
import { useDevToolsGuard } from "./useDevToolsGuard";

const EMPTY_FLAGS = {};

/**
 * Orchestrates the browser-based document protection controls for DocumentRPD.
 *
 * Driven entirely by RShield: the SENDER turns it on per document on the
 * Send / Generate tab and picks the options (Focus Protection, Resume on full
 * screen, Focus Loss Limit, Ban Reader, Notify Sender). Those values arrive on
 * the /docs/rpddata response and are passed in as `rShield`.
 *
 * When the document was not sent with RShield - or the AppContext kill-switch
 * is off - this returns { active: false }, every underlying hook is disabled,
 * and the existing DocumentRPD behavior is completely unchanged. There is no
 * per-reader-email configuration any more.
 *
 * @param {object} opts
 * @param {string}  opts.email          reader login email (audit / watermark)
 * @param {object}  opts.containerRef   ref to the protected content element
 * @param {string}  opts.documentId
 * @param {string}  opts.sessionId
 * @param {string} [opts.openId]        open record id, used to attribute the
 *        focus log rows written to the rShieldFocusLog collection
 * @param {object} [opts.rShield]       { enabled, options } from parseRShieldPayload
 * @param {object} [opts.translations]  optional map for overlay copy
 * @param {string} [opts.documentName]
 * @param {string} [opts.ipAddress]
 * @param {function} [opts.onEvent]     called for each guard/audit event
 */
export function useDocumentProtection({
  email,
  containerRef,
  documentId = "",
  sessionId = "",
  openId = "",
  pageNumber = 0,
  onRedirect,
  rShield = null,
  translations = {},
  documentName = "",
  ipAddress = "",
  onEvent,
} = {}) {
  const config = useMemo(() => {
    const flags = resolveRShieldConfig(rShield, isProtectionEnabled());
    if (!flags) return null;
    return {
      ...flags,
      __auditApiUrl: getAuditApiUrl(),
      __email: (email || "").trim().toLowerCase(),
    };
  }, [rShield, email]);

  const active = !!config;
  const flags = config || EMPTY_FLAGS;

  const audit = useMemo(() => {
    if (!active) return { log: () => {} };
    return createAuditLogger({
      apiUrl: flags.__auditApiUrl || "",
      email: flags.__email || email || "",
      documentId,
      sessionId,
    });
  }, [active, flags.__auditApiUrl, flags.__email, email, documentId, sessionId]);

  const auditEnabled = active && flags.auditLogging !== false;

  // Persists focus loss / capture events to the rShieldFocusLog collection.
  // Separate from the generic audit logger: these rows are attributed to the
  // open record server side, drive the Ban Reader action and feed the Notify
  // Sender capture detection email.
  // The page number is read through a ref at send time so page navigation
  // does not rebuild the reporter.
  const pageNumberRef = useRef(pageNumber);
  pageNumberRef.current = pageNumber;
  const onRedirectRef = useRef(onRedirect);
  onRedirectRef.current = onRedirect;
  const focusReporter = useMemo(
    () =>
      createRShieldFocusReporter({
        documentId,
        openId,
        sessionId,
        getPageNumber: () => pageNumberRef.current,
        onRedirect: (url) => {
          if (typeof onRedirectRef.current === "function") {
            onRedirectRef.current(url);
          } else {
            window.open(url, "_self");
          }
        },
      }),
    [documentId, openId, sessionId]
  );

  // Keep the latest onEvent callback in a ref so guard effects don't re-run
  // when the parent passes a new function identity on each render.
  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  // `log` both audits (Feature 10) and notifies the host component so it can
  // surface UI (e.g. a "Print is clicked" alert) using its own controls.
  const log = useCallback(
    (event, details) => {
      if (auditEnabled) audit.log(event, details);
      if (active && typeof onEventRef.current === "function") {
        try {
          onEventRef.current(event, details);
        } catch (e) {
          /* never let host callback break protection */
        }
      }
    },
    [auditEnabled, audit, active]
  );

  // Log document open once when protection becomes active.
  const openedRef = useRef(false);
  useEffect(() => {
    if (active && !openedRef.current) {
      openedRef.current = true;
      log(AuditEvents.DOCUMENT_OPEN);
    }
  }, [active, log]);

  const getTarget = useCallback(
    () => (containerRef && containerRef.current) || null,
    [containerRef]
  );

  // --- Full screen (RShield: "Resume on full screen") -------------------
  // The requirement applies from the moment the document opens: until the
  // reader is in full screen the RPD stays blurred behind an overlay carrying
  // an "Enter Full Screen" button. The same overlay reappears whenever they
  // leave full screen, so viewing resumes only on returning to it.
  const requireFullscreen = active && flags.resumeOnFullScreen === true;
  const { isFullscreen, requestFullscreen } = useFullscreen({
    enabled: requireFullscreen,
    getTarget: () => document.documentElement,
    onChange: (entered) => {
      log(entered ? AuditEvents.FULLSCREEN_ENTER : AuditEvents.FULLSCREEN_EXIT);
      if (entered) focusReporter.reportFirstFullScreen();
    },
  });

  // --- Focus Protection: window focus ----------------------------------
  const { isFocused } = useWindowFocus({
    enabled: active && flags.blurOnWindowBlur === true,
    onChange: (focused) => {
      log(focused ? AuditEvents.WINDOW_FOCUS : AuditEvents.WINDOW_BLUR);
    },
  });

  // --- DevTools ---------------------------------------------------------
  // OFF for RShield (devToolsDetection is false there). Detection is a
  // heuristic that can false positive, and there is no sender option behind
  // it, so it must never gate viewing on its own.
  const { isDevToolsOpen } = useDevToolsGuard({
    enabled: active && flags.devToolsDetection === true,
    onChange: (open) => {
      if (open) log(AuditEvents.DEVTOOLS_ATTEMPT, { Source: "PanelDetected" });
    },
  });

  // --- Focus Protection: page visibility -------------------------------
  const { isVisible } = usePageVisibility({
    enabled: active && flags.blurOnHidden === true,
    onChange: (visible) => {
      log(visible ? AuditEvents.PAGE_VISIBLE : AuditEvents.PAGE_HIDDEN);
    },
  });

  // --- DOM guards -------------------------------------------------------
  // OFF for RShield: the RPD already applies its own restrictions and none of
  // these are sender options.
  useContextMenuGuard({
    enabled: active && flags.disableRightClick === true,
    getTarget,
    onBlocked: () => log(AuditEvents.RIGHT_CLICK),
  });
  useTextSelectionGuard({
    enabled: active && flags.disableTextSelection === true,
    getTarget,
    onBlocked: () => log(AuditEvents.TEXT_SELECTION_ATTEMPT),
  });
  useDragDropGuard({
    enabled: active && flags.disableDragDrop === true,
    getTarget,
    onBlocked: () => log(AuditEvents.DRAG_ATTEMPT),
  });

  // --- RShield: focus loss limit / resume on full screen / ban reader ---
  // Counting is driven by the focus + visibility states resolved above so one
  // alt-tab is a single focus loss, whichever event the browser fires first.
  const {
    focusLossCount,
    focusLossRemaining,
    isRevoked,
    revokedReason,
    awaitingFullScreen,
    isSuppressed,
    beginInAppAction,
    endInAppAction,
    reportCaptureAttempt,
  } = useFocusLossLimit({
    // Enabled for the whole of RShield, not just Focus Protection: Capture
    // Detection / Ban Reader needs the same session lock. Counting itself is
    // gated on focusProtection inside the hook.
    enabled: active,
    focusProtection: flags.focusProtection === true,
    focusLossLimit: flags.focusLossLimit === true,
    focusLossLimitValue: flags.focusLossLimitValue,
    resumeOnFullScreen: flags.resumeOnFullScreen === true,
    banReader: flags.banReader === true,
    isFullscreen,
    storageKey: `${documentId}_${sessionId}`,
    onFocusLoss: (count, limit) => {
      log(AuditEvents.RSHIELD_FOCUS_LOSS, {
        FocusLossCount: count,
        FocusLossLimit: limit || null,
      });
      focusReporter.report(AuditEvents.RSHIELD_FOCUS_LOSS, {
        FocusLossCount: count,
        FocusLossLimit: limit || 0,
      });
    },
    onRevoked: (reason, details) => {
      log(AuditEvents.RSHIELD_ACCESS_REVOKED, { Reason: reason, ...details });
      if (reason !== "CaptureDetected") {
        focusReporter.report(AuditEvents.RSHIELD_ACCESS_REVOKED, {
          Reason: reason,
          FocusLossCount: details && details.FocusLossCount,
          FocusLossLimit: details && details.FocusLossLimit,
        });
      }
      // Notify Sender is a sender-side option: the reader only raises the
      // event, the notification itself is driven off the audit stream.
      if (flags.notifySender === true) {
        log(AuditEvents.RSHIELD_NOTIFY_SENDER, { Reason: reason, ...details });
      }
    },
  });

  // --- Keyboard guard + Capture Detection -------------------------------
  // Under RShield this runs in capture-only mode: screen capture shortcuts are
  // detected (feeding Ban Reader / Notify Sender) and nothing is blocked, so
  // print and copy keep behaving as the document's own permissions dictate.
  useKeyboardGuard({
    enabled: active && (flags.keyboardProtection === true || flags.captureDetection === true),
    captureOnly: flags.keyboardProtection !== true,
    onBlocked: (event) => {
      log(event);
      // Capture Detection -> Ban Reader / Notify Sender. OS-level capture
      // cannot be blocked by the browser, so the shortcut is what we act on.
      if (event === AuditEvents.SCREENSHOT_ATTEMPT && active) {
        log(AuditEvents.RSHIELD_CAPTURE_DETECTED);
        // Logged for every capture attempt, whether or not Ban Reader is on.
        // When it is on, the API bans the recipient off the back of this row
        // (Status = Banned, Enabled = false on the document).
        focusReporter.report(AuditEvents.RSHIELD_CAPTURE_DETECTED, {
          Reason: "CaptureDetected",
        });
        if (flags.notifySender === true) {
          log(AuditEvents.RSHIELD_NOTIFY_SENDER, { Reason: "CaptureDetected" });
        }
        reportCaptureAttempt({ Source: "KeyboardShortcut" });
      }
    },
  });

  // --- Blur state resolution --------------------------------------------
  // Focus Protection: pause viewing while the reader is away.
  const hidden = active && !isSuppressed && flags.blurOnHidden === true && !isVisible;
  const unfocused = active && !isSuppressed && flags.blurOnWindowBlur === true && !isFocused;
  // Access revoked (focus loss limit exceeded, or capture detected with Ban
  // Reader on) - the view stays locked for the rest of the session.
  const revoked = active && isRevoked;
  // "Resume on full screen": enforced from open and on every full screen exit.
  const needFullscreen =
    requireFullscreen && !revoked && !isSuppressed && !isFullscreen;
  // Focus was lost while still in full screen - hold the view until the reader
  // is back on the RPD.
  const needResumeFullScreen =
    active && !revoked && !needFullscreen && awaitingFullScreen;
  // DevTools panel is open.
  const inspecting = active && isDevToolsOpen;
  const isBlurred =
    active &&
    (revoked ||
      inspecting ||
      needFullscreen ||
      needResumeFullScreen ||
      hidden ||
      unfocused);

  // Apply/remove blur class on the protected container.
  useEffect(() => {
    if (!active) return undefined;
    const el = getTarget();
    if (!el) return undefined;
    if (isBlurred) el.classList.add("dp-blurred");
    else el.classList.remove("dp-blurred");
    return () => {
      if (el) el.classList.remove("dp-blurred");
    };
  }, [active, isBlurred, getTarget]);

  // --- Overlay copy (prefers translations, falls back to defaults) -----
  const t = translations || {};
  let overlayProps = { visible: false };
  if (isBlurred) {
    if (revoked) {
      // Terminal state for the session - no action button, nothing the reader
      // can dismiss.
      //
      // The two revoke reasons need different guidance. A focus loss limit
      // breach only ends the session, so reopening the document is enough. A
      // capture attempt with Ban Reader on has also disabled the recipient on
      // the document server side, so reopening will not help - the reader has
      // to go back to the owner.
      const bannedForCapture = revokedReason === "CaptureDetected";
      overlayProps = {
        visible: true,
        title: t.Reader_RShieldAccessRevoked || "Access Revoked",
        message: bannedForCapture
          ? t.Reader_RShieldBannedMessage ||
            "For security reasons, this document is no longer available in the current session. Please contact RPD owner."
          : t.Reader_RShieldAccessRevokedMessage ||
            "For security reasons, this document is no longer available in the current session. Please reopen it to continue.",
      };
    } else if (inspecting) {
      overlayProps = {
        visible: true,
        title: t.Reader_ProtectedDocument || "Protected Document",
        message:
          t.Reader_DevToolsOpenMessage ||
          "Viewing is paused while the browser developer tools are open. Close them to resume reading.",
      };
    } else if (needFullscreen || needResumeFullScreen) {
      overlayProps = {
        visible: true,
        message:
          t.Reader_ReturnToFullscreen ||
          "This document can only be viewed in Full Screen Mode.",
        actionLabel: t.Reader_EnterFullScreen || "Enter Full Screen",
        onAction: requestFullscreen,
      };
    } else if (hidden) {
      overlayProps = {
        visible: true,
        title: t.Reader_ProtectedDocument || "Protected Document",
        message:
          t.Reader_ProtectedDocumentAccess ||
          "Viewing is paused while this page is hidden.",
      };
    } else if (unfocused) {
      overlayProps = {
        visible: true,
        title: t.Reader_ProtectedDocument || "Protected Document",
        message:
          t.Reader_ProtectedDocumentAccess ||
          "Viewing is paused while the window is not in focus.",
      };
    }
  }

  // Capture Detection warning badge - shown while Notify Sender is on, so the
  // reader knows before attempting a capture that the owner will be told.
  const captureWarningProps = {
    enabled: active && flags.notifySender === true && !revoked,
    message:
      t.Reader_RShieldCaptureWarning ||
      "Warning: Screen capture is not permitted by doc owner. Doc owner will be notified if you attempt to use screen capture tools.",
  };

  const watermarkProps = {
    enabled: active && flags.watermark === true,
    email: flags.__email || email || "",
    sessionId,
    documentName,
    ipAddress,
  };

  return {
    active,
    config: flags,
    isBlurred,
    isFullscreen,
    requestFullscreen,
    overlayProps,
    captureWarningProps,
    watermarkProps,
    audit: log,
    AuditEvents,

    // --- RShield ---
    isRShield: active,
    isRevoked: revoked,
    /** FocusLossLimitExceeded | CaptureDetected | "" */
    revokedReason: revoked ? revokedReason : "",
    /** Full screen is required and the reader is not in it right now. */
    needsFullScreen: needFullscreen,
    /** DevTools panel detected as open. */
    isDevToolsOpen: inspecting,
    /**
     * True when the page image must NOT be in the DOM at all. Host components
     * render a same-size placeholder instead of the <img> while this is set.
     *
     * Deliberately narrower than isBlurred: only the states where the content
     * is actually at risk of being read out (DevTools open) or permanently
     * withdrawn (revoked). Transient pauses - window blur, tab switch, leaving
     * full screen - are covered by the blur and the overlay instead, because
     * unmounting the image on every alt-tab churns the image reload and the
     * dimension recalculation it triggers.
     */
    hideContent: revoked || inspecting,
    awaitingFullScreen: needResumeFullScreen,
    focusLossCount,
    focusLossRemaining,
    /** Call from any other capture detector (e.g. screen recording checks). */
    reportCaptureAttempt,
    beginInAppAction,
    endInAppAction,
  };
}

export default useDocumentProtection;
